import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Clock, MapPin, Phone, Quote, Sprout, Users } from "lucide-react";
import { Marquee } from "@/components/Marquee";
import { ContactForm } from "@/components/ContactForm";
import { Eyebrow, Page, Reveal } from "@/components/Reveal";
import { EMAIL, IMAGES, MAILTO, PHONE_DISPLAY, PHONE_TEL, REGIONS, SERVICES, TAGLINE, TESTIMONIALS } from "@/constants/site";

const EASE = [0.22, 1, 0.36, 1];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({ y: 0, transition: { delay: 0.25 + i * 0.14, duration: 1, ease: EASE } }),
};

const fadeVariants = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: 0.8 + i * 0.15, duration: 0.8, ease: EASE } }),
};

const MANIFESTO = [
  {
    num: "01",
    title: "Crews You Can Count On",
    copy: "When harvest windows are measured in days, no-shows cost you the crop. Our crews arrive on time, every time — ready to work from the first hour.",
  },
  {
    num: "02",
    title: "Experienced Agricultural Hands",
    copy: "Farm fields, greenhouse rows and cannery lines all demand different skills. We match workers who already know the job to the operation that needs them.",
  },
  {
    num: "03",
    title: "Local & Fast Across the South Coast",
    copy: "Based in the region we serve. From the Fraser Valley to Greater Vancouver, we dispatch crews quickly — often on next-day notice.",
  },
];

const Hero = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  const lines = [
    <>Reliable crews for</>,
    <>
      farms, <span className="italic text-[#86EFAC]">greenhouses</span>
    </>,
    <>
      &amp; <span className="italic text-[#86EFAC]">canneries</span>.
    </>,
  ];

  return (
    <section ref={heroRef} data-testid="hero-section" className="relative flex min-h-screen items-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img src={IMAGES.harvestCrew} alt="Farm crew harvesting in the field" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0F291E]/80" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F291E] via-transparent to-[#0F291E]/70" />
      </motion.div>

      <div className="relative mx-auto w-full max-w-7xl px-6 pb-24 pt-36 sm:px-10">
        <motion.p
          custom={0}
          initial="hidden"
          animate="show"
          variants={fadeVariants}
          className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-[#86EFAC]"
        >
          Barnala Labour Contracting — British Columbia
        </motion.p>

        <h1 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight text-[#F6F4EE] sm:text-5xl lg:text-6xl">
          {lines.map((content, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span className="block" custom={i} initial="hidden" animate="show" variants={lineVariants}>
                {content}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          custom={1}
          initial="hidden"
          animate="show"
          variants={fadeVariants}
          className="mt-6 max-w-xl text-base leading-relaxed text-[#F6F4EE]/75 sm:text-lg"
        >
          We supply dependable labour for farms, greenhouses and canneries across the Lower Mainland, Fraser Valley and
          Greater Vancouver — one call, and your crew is handled.
        </motion.p>

        <motion.p
          custom={2}
          initial="hidden"
          animate="show"
          variants={fadeVariants}
          data-testid="hero-tagline"
          className="mt-8 font-display text-xl font-semibold italic tracking-tight text-[#86EFAC] sm:text-2xl"
        >
          “{TAGLINE}”
        </motion.p>

        <motion.div custom={3} initial="hidden" animate="show" variants={fadeVariants} className="mt-8 flex flex-wrap items-center gap-4">
          <a
            href={PHONE_TEL}
            data-testid="hero-call-button"
            className="inline-flex items-center gap-2 rounded-full bg-[#22C55E] px-7 py-4 text-sm font-semibold text-[#0F291E] transition-transform duration-300 hover:scale-105"
          >
            <Phone className="h-4 w-4" />
            Call {PHONE_DISPLAY}
          </a>
          <Link
            to="/contact"
            data-testid="hero-request-crew-button"
            className="group inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-4 text-sm font-semibold text-[#F6F4EE] transition-colors duration-300 hover:border-[#86EFAC] hover:text-[#86EFAC]"
          >
            Request a Crew
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </motion.div>

        <motion.div
          custom={4}
          initial="hidden"
          animate="show"
          variants={fadeVariants}
          className="mt-16 grid max-w-2xl grid-cols-1 gap-6 border-t border-white/15 pt-8 sm:grid-cols-3"
        >
          {[
            { icon: MapPin, label: "3 regions served", sub: "Lower Mainland · Fraser Valley · GVA" },
            { icon: Users, label: "Flexible crew sizes", sub: "From 2 workers to full harvest crews" },
            { icon: Clock, label: "7-day dispatch", sub: "Seasonal peaks & short-notice fills" },
          ].map(({ icon: Icon, label, sub }) => (
            <div key={label}>
              <Icon className="h-5 w-5 text-[#22C55E]" />
              <p className="mt-3 text-sm font-semibold text-[#F6F4EE]">{label}</p>
              <p className="mt-1 text-xs leading-relaxed text-[#F6F4EE]/55">{sub}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <Page>
      <Hero />

      <Marquee
        items={[
          "Farm Labour",
          "Greenhouse Crews",
          "Cannery Lines",
          "Harvest & Picking",
          "Packing & Sorting",
          "Lower Mainland",
          "Fraser Valley",
          "Greater Vancouver",
        ]}
      />

      <section data-testid="manifesto-section" className="px-6 py-24 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <Eyebrow>The Barnala Standard</Eyebrow>
          </Reveal>
          <div className="mt-12 divide-y divide-white/10 border-y border-white/10">
            {MANIFESTO.map((item, i) => (
              <Reveal key={item.num} delay={i * 0.1}>
                <div className="group grid gap-4 py-10 transition-colors duration-500 hover:bg-[#163B2C]/40 sm:grid-cols-[100px_1fr_1.2fr] sm:items-baseline sm:gap-10 sm:px-6">
                  <span className="font-mono text-sm font-semibold text-[#22C55E]">{item.num}</span>
                  <h3 className="font-display text-2xl font-semibold tracking-tight text-[#F6F4EE] transition-colors duration-300 group-hover:text-[#86EFAC] sm:text-3xl">
                    {item.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-[#F6F4EE]/65 sm:text-base">{item.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="services-preview-section" className="bg-[#0B1F17] px-6 py-24 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Reveal>
                <Eyebrow>What We Do</Eyebrow>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-[#F6F4EE] sm:text-4xl lg:text-5xl">
                  Labour for every row,
                  <br />
                  line and season.
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <Link
                to="/services"
                data-testid="services-view-all-link"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-[#86EFAC]"
              >
                View all services
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </Reveal>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.12}>
                <Link
                  to="/services"
                  data-testid={`service-card-${s.id}`}
                  className="group block overflow-hidden rounded-2xl border border-white/10 bg-[#163B2C]/50 transition-colors duration-500 hover:border-[#22C55E]/50"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F291E]/90 to-transparent" />
                    <span className="absolute left-5 top-5 font-mono text-xs font-semibold text-[#86EFAC]">{s.num}</span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-xl font-semibold tracking-tight text-[#F6F4EE] sm:text-2xl">
                      {s.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-[#F6F4EE]/60">{s.blurb}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="regions-section" className="px-6 py-24 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <Eyebrow>Where We Work</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold tracking-tight text-[#F6F4EE] sm:text-4xl lg:text-5xl">
              Proudly serving BC's South Coast.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {REGIONS.map((r, i) => (
              <Reveal key={r.id} delay={i * 0.12}>
                <div
                  data-testid={`region-card-${r.id}`}
                  className="group h-full rounded-2xl border border-white/10 bg-[#163B2C]/40 p-8 transition-all duration-500 hover:-translate-y-1 hover:border-[#22C55E]/50"
                >
                  <MapPin className="h-6 w-6 text-[#22C55E]" />
                  <h3 className="mt-5 font-display text-xl font-semibold tracking-tight sm:text-2xl">{r.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#F6F4EE]/60">{r.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="testimonials-preview-section" className="bg-[#0B1F17] px-6 py-24 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <Eyebrow>Word From The Field</Eyebrow>
          </Reveal>
          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {TESTIMONIALS.slice(0, 3).map((t, i) => (
              <Reveal key={i} delay={i * 0.12}>
                <figure
                  data-testid={`home-testimonial-card-${i}`}
                  className="flex h-full flex-col rounded-2xl border border-white/10 bg-[#163B2C]/40 p-8"
                >
                  <Quote className="h-6 w-6 text-[#22C55E]" />
                  <blockquote className="mt-5 flex-1 font-display text-lg italic leading-relaxed text-[#F6F4EE]/85">
                    “{t.quote}”
                  </blockquote>
                  <figcaption className="mt-6 border-t border-white/10 pt-4">
                    <p className="text-sm font-semibold text-[#F6F4EE]">{t.name}</p>
                    <p className="mt-1 text-xs text-[#86EFAC]/80">{t.place}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="contact-teaser-section" className="px-6 py-24 sm:px-10 lg:px-16">
        <div className="mx-auto grid max-w-6xl gap-14 lg:grid-cols-2 lg:items-start">
          <div>
            <Reveal>
              <Eyebrow>Get Crewed Up</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-[#F6F4EE] sm:text-4xl lg:text-5xl">
                Need hands this season?
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-5 max-w-md text-base leading-relaxed text-[#F6F4EE]/65">
                Tell us the job — crop, location, crew size and dates — and we'll get back to you fast. Prefer to talk?
                We answer the phone.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-8 space-y-4">
                <a
                  href={PHONE_TEL}
                  data-testid="teaser-call-link"
                  className="flex items-center gap-3 text-lg font-semibold text-[#86EFAC] transition-colors hover:text-[#22C55E]"
                >
                  <Phone className="h-5 w-5" />
                  {PHONE_DISPLAY}
                </a>
                <a
                  href={MAILTO}
                  data-testid="teaser-email-link"
                  className="flex items-center gap-3 text-sm text-[#F6F4EE]/70 transition-colors hover:text-[#86EFAC]"
                >
                  <Sprout className="h-4 w-4 text-[#22C55E]" />
                  {EMAIL}
                </a>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <div className="rounded-2xl border border-white/10 bg-[#163B2C]/40 p-6 sm:p-8">
              <ContactForm compact />
            </div>
          </Reveal>
        </div>
      </section>
    </Page>
  );
}
